import { useState, useMemo } from "react";
import {
  Search, Download, AlertTriangle, TrendingDown, CheckCircle2,
  ArrowUpDown, Users, ShieldAlert, ChevronRight
} from "lucide-react";
import {
  CLUSTERS,
  extractSevenIndicators,
  checkClusterMembership,
  calcDynamicTrend
} from "./clinicalClusters";
import { buildCsv, downloadCsv } from "@/pages/Client/helpers";

export default function DoctorPopulationTable({
  users = [],
  clients = [],
  selectedClient = "all",
  onSelectUserForRecord,
  onOpenIhoModal,
}) {
  const [activeCluster, setActiveCluster] = useState("endocrine"); // Default al clúster de endocrinología para la visita del martes
  const [trendFilter, setTrendFilter] = useState("all"); // all | stagnant | improving | deteriorating | baseline
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("trendPriority"); // trendPriority | name | imc | glucose | pas | stress
  const [sortAsc, setSortAsc] = useState(false);

  // Procesar y enriquecer todos los colaboradores con los 7 indicadores, clústeres y tendencia
  const processedUsers = useMemo(() => {
    const getClientName = (id) => {
      if (id === "demo-enterprise") return "TechCorp Global B2B (Demo)";
      const c = clients.find((item) => item.id === id);
      return c?.name || "Organización General";
    };

    return users.map((u) => {
      const indicators = extractSevenIndicators(u);
      const clusters = checkClusterMembership(indicators);
      const trend = calcDynamicTrend(indicators, u);

      // Prioridad numérica para ordenamiento de gravedad de tendencia
      let trendPriority = 1;
      if (trend.status === "stagnant") trendPriority = 4; // Estancamiento tiene alta prioridad de acción con IHO
      else if (trend.status === "deteriorating") trendPriority = 3;
      else if (trend.status === "baseline") trendPriority = 2;
      else if (trend.status === "improving") trendPriority = 1;

      return {
        rawUser: u,
        indicators,
        clusters,
        trend,
        trendPriority,
        companyName: getClientName(u.clientId || indicators.companyId),
      };
    });
  }, [users, clients]);

  // Conteos para los pills de los clústeres
  const clusterCounts = useMemo(() => {
    let all = processedUsers.length;
    let endocrine = 0;
    let cardio = 0;
    let mental = 0;

    processedUsers.forEach((item) => {
      if (item.clusters.endocrine) endocrine++;
      if (item.clusters.cardio) cardio++;
      if (item.clusters.mental) mental++;
    });

    return { all, endocrine, cardio, mental };
  }, [processedUsers]);

  // Filtrado compuesto (Clúster + Búsqueda + Tendencia Dinámica)
  const filteredUsers = useMemo(() => {
    return processedUsers.filter((item) => {
      // 1. Filtro por Clúster Activo
      if (activeCluster === "endocrine" && !item.clusters.endocrine) return false;
      if (activeCluster === "cardio" && !item.clusters.cardio) return false;
      if (activeCluster === "mental" && !item.clusters.mental) return false;

      // 2. Filtro por Tendencia Dinámica
      if (trendFilter !== "all" && item.trend.status !== trendFilter) return false;

      // 3. Filtro por Búsqueda
      if (search) {
        const s = search.toLowerCase();
        const matchesName = item.indicators.name.toLowerCase().includes(s);
        const matchesEmail = item.indicators.email.toLowerCase().includes(s);
        const matchesCompany = item.companyName.toLowerCase().includes(s);
        if (!matchesName && !matchesEmail && !matchesCompany) return false;
      }

      return true;
    });
  }, [processedUsers, activeCluster, trendFilter, search]);

  // Ordenamiento de la tabla
  const sortedUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) => {
      let valA, valB;
      if (sortField === "trendPriority") {
        valA = a.trendPriority;
        valB = b.trendPriority;
      } else if (sortField === "name") {
        valA = a.indicators.name.toLowerCase();
        valB = b.indicators.name.toLowerCase();
      } else if (sortField === "imc") {
        valA = a.indicators.imc || 0;
        valB = b.indicators.imc || 0;
      } else if (sortField === "glucose") {
        valA = a.indicators.glucemia || 0;
        valB = b.indicators.glucemia || 0;
      } else if (sortField === "pas") {
        valA = a.indicators.pas || 0;
        valB = b.indicators.pas || 0;
      } else if (sortField === "stress") {
        valA = a.indicators.stressIndex || 0;
        valB = b.indicators.stressIndex || 0;
      } else {
        valA = a.indicators.name;
        valB = b.indicators.name;
      }

      if (valA < valB) return sortAsc ? -1 : 1;
      if (valA > valB) return sortAsc ? 1 : -1;
      return 0;
    });
  }, [filteredUsers, sortField, sortAsc]);

  // Estadísticas del Clúster Actual
  const clusterStats = useMemo(() => {
    const total = filteredUsers.length;
    const stagnantCount = filteredUsers.filter((u) => u.trend.status === "stagnant").length;
    const improvingCount = filteredUsers.filter((u) => u.trend.status === "improving").length;
    const deterioratingCount = filteredUsers.filter((u) => u.trend.status === "deteriorating").length;

    // Promedios
    const validImcs = filteredUsers.map((u) => u.indicators.imc).filter((n) => n != null);
    const avgImc = validImcs.length ? (validImcs.reduce((a, b) => a + b, 0) / validImcs.length).toFixed(1) : "—";

    const validGlus = filteredUsers.map((u) => u.indicators.glucemia).filter((n) => n != null);
    const avgGlu = validGlus.length ? Math.round(validGlus.reduce((a, b) => a + b, 0) / validGlus.length) : "—";

    return { total, stagnantCount, improvingCount, deterioratingCount, avgImc, avgGlu };
  }, [filteredUsers]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(false);
    }
  };

  // Exportar reporte de la tabla a CSV
  const handleExportCsv = () => {
    const headers = [
      "Colaborador", "Email", "Empresa", "Edad", "Sexo",
      "Peso (kg)", "IMC (kg/m2)", "Categoria IMC",
      "Cintura (cm)", "Ratio WHtR", "Categoria Grasa Visceral",
      "Glicemia Basal (mg/dL)", "Categoria Glicemia ADA",
      "Presion Sistolica (mmHg)", "Presion Diastolica (mmHg)", "Categoria PA AHA",
      "Colesterol (mg/dL)", "Trigliceridos (mg/dL)",
      "Indice Estres Baevsky", "Categoria Estres",
      "Tendencia Dinamica", "Accion Sugerida IHO"
    ];

    const rows = sortedUsers.map((item) => {
      const ind = item.indicators;
      return [
        ind.name, ind.email, item.companyName, ind.edad, ind.sexo,
        ind.peso || "", ind.imc || "", ind.imcBadge.label,
        ind.cintura || "", ind.whtr || "", ind.whtrBadge.label,
        ind.glucemia || "", ind.gluBadge.label,
        ind.pas || "", ind.pad || "", ind.bpBadge.label,
        ind.colesterol || "", ind.trigliceridos || "",
        ind.stressIndex || "", ind.stressBadge.label,
        item.trend.label, item.trend.actionNeeded
      ];
    });

    const csvContent = buildCsv(headers, rows);
    const filename = `WE360_Reporte_Cluster_${activeCluster}_${new Date().toISOString().slice(0, 10)}.csv`;
    downloadCsv(filename, csvContent);
  };

  return (
    <div className="space-y-5">
      {/* 1. SELECTOR DE CLÚSTERES CLÍNICOS ESPECIALIZADOS */}
      <div className="p-2 sm:p-2.5 rounded-3xl bg-white/80 border border-stone-200/80 shadow-sm backdrop-blur-md">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {CLUSTERS.map((cl) => {
            const isCurrent = activeCluster === cl.id;
            const count = clusterCounts[cl.id] || 0;
            const percent = clusterCounts.all > 0 ? Math.round((count / clusterCounts.all) * 100) : 0;

            return (
              <button
                key={cl.id}
                onClick={() => setActiveCluster(cl.id)}
                className={`p-3.5 rounded-2xl text-left transition-all cursor-pointer border flex flex-col justify-between ${
                  isCurrent
                    ? "bg-white shadow-md border-amber-400/80 ring-2 ring-amber-400/30"
                    : "bg-white/40 hover:bg-white/80 border-stone-200/60"
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span 
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold"
                      style={{ background: cl.bg, color: cl.color }}
                    >
                      {cl.id === "endocrine" ? "🩺" : (cl.id === "cardio" ? "🫀" : (cl.id === "mental" ? "🧠" : "📋"))}
                    </span>
                    <span className="text-xs font-black text-stone-900 tracking-tight leading-snug">
                      {cl.shortLabel}
                    </span>
                  </div>
                  <span 
                    className="text-[11px] font-black px-2 py-0.5 rounded-full"
                    style={{ background: isCurrent ? cl.bg : "#F3F4F6", color: isCurrent ? cl.color : "#4B5563" }}
                  >
                    {count} ({percent}%)
                  </span>
                </div>
                <p className="text-[10px] text-stone-500 line-clamp-1 leading-normal font-medium">
                  {cl.specialist ? `${cl.specialist}` : cl.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. BARRA DE KPI DEL CLÚSTER SELECCIONADO */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-3.5 rounded-2xl bg-white border border-stone-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400">Población en Clúster</p>
            <p className="text-xl font-black text-stone-900 mt-0.5">{clusterStats.total}</p>
          </div>
          <div className="w-8 h-8 rounded-xl bg-cyan-50 text-[#00628F] flex items-center justify-center font-bold text-xs">
            {clusterCounts.all > 0 ? `${Math.round((clusterStats.total / clusterCounts.all) * 100)}%` : "0%"}
          </div>
        </div>

        {/* ALERTA DE ESTANCAMIENTO (Foco para intervención de IHO) */}
        <div 
          onClick={() => setTrendFilter(trendFilter === "stagnant" ? "all" : "stagnant")}
          className={`p-3.5 rounded-2xl border shadow-sm flex items-center justify-between cursor-pointer transition-all ${
            trendFilter === "stagnant"
              ? "bg-amber-100 border-amber-400 ring-2 ring-amber-400/40"
              : "bg-amber-50/70 hover:bg-amber-100/70 border-amber-200"
          }`}
          title="Haga clic para filtrar colaboradores en estancamiento"
        >
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-800 flex items-center gap-1">
              <span>Estancamiento</span>
              <AlertTriangle size={11} className="text-amber-600 animate-pulse" />
            </p>
            <p className="text-xl font-black text-amber-950 mt-0.5">{clusterStats.stagnantCount}</p>
          </div>
          <div className="text-[10px] font-bold text-amber-900 bg-amber-200/70 px-2 py-0.5 rounded-lg">
            Prioridad IHO
          </div>
        </div>

        <div className="p-3.5 rounded-2xl bg-white border border-stone-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 flex items-center gap-1">
              <span>Mejorando</span>
              <TrendingDown size={12} />
            </p>
            <p className="text-xl font-black text-emerald-700 mt-0.5">{clusterStats.improvingCount}</p>
          </div>
          <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold text-xs">
            <CheckCircle2 size={16} />
          </div>
        </div>

        <div className="p-3.5 rounded-2xl bg-white border border-stone-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400">Promedios Clúster</p>
            <p className="text-xs font-black text-stone-800 mt-1">
              IMC: <span className="text-amber-700">{clusterStats.avgImc}</span> | Glu: <span className="text-amber-700">{clusterStats.avgGlu}</span>
            </p>
          </div>
          <div className="w-8 h-8 rounded-xl bg-stone-100 text-stone-600 flex items-center justify-center font-bold text-xs">
            📊
          </div>
        </div>
      </div>

      {/* 3. BARRA DE CONTROL, BÚSQUEDA Y EXPORTACIÓN */}
      <div className="p-3 rounded-2xl bg-white border border-stone-200/90 shadow-sm flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        {/* Buscador */}
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Buscar por nombre, correo o empresa..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-xl text-xs font-medium text-stone-800 bg-stone-50 border border-stone-200 outline-none focus:bg-white focus:border-cyan-600 transition-all"
          />
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        </div>

        {/* Botonera de Filtro de Tendencia */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0">
          <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider shrink-0 mr-1">
            Tendencia:
          </span>
          {[
            { id: "all", label: "Todas" },
            { id: "stagnant", label: "Estancamiento ⚠️" },
            { id: "improving", label: "Mejorando ↘" },
            { id: "deteriorating", label: "Deterioro ↗" },
            { id: "baseline", label: "Basal ⏳" },
          ].map((t) => {
            const isSelected = trendFilter === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTrendFilter(t.id)}
                className={`px-2.5 py-1.5 rounded-xl text-[11px] font-bold whitespace-nowrap transition-all cursor-pointer border ${
                  isSelected
                    ? "bg-stone-900 text-white border-stone-900 shadow-sm"
                    : "bg-white text-stone-600 hover:bg-stone-50 border-stone-200"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Botón de Exportación CSV */}
        <button
          onClick={handleExportCsv}
          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-stone-700 bg-stone-100 hover:bg-stone-200 border border-stone-200 transition-all cursor-pointer shrink-0 active:scale-95"
          title="Descargar datos en formato CSV para análisis externo"
        >
          <Download size={13} />
          <span>Exportar CSV</span>
        </button>
      </div>

      {/* 4. TABLA DINÁMICA DE LOS 7 INDICADORES */}
      <div className="rounded-3xl bg-white border border-stone-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-stone-50/90 border-b border-stone-200 text-[10px] font-bold uppercase tracking-wider text-stone-500 select-none">
                <th className="py-3 px-4">
                  <button 
                    onClick={() => toggleSort("name")} 
                    className="flex items-center gap-1 hover:text-stone-900 transition-colors cursor-pointer"
                  >
                    <span>Colaborador</span>
                    <ArrowUpDown size={11} />
                  </button>
                </th>
                <th className="py-3 px-3">
                  <button 
                    onClick={() => toggleSort("imc")} 
                    className="flex items-center gap-1 hover:text-stone-900 transition-colors cursor-pointer"
                  >
                    <span>1. Peso & IMC</span>
                    <ArrowUpDown size={11} />
                  </button>
                </th>
                <th className="py-3 px-3">2. Grasa Visceral (WHtR)</th>
                <th className="py-3 px-3">
                  <button 
                    onClick={() => toggleSort("glucose")} 
                    className="flex items-center gap-1 hover:text-stone-900 transition-colors cursor-pointer"
                  >
                    <span>3. Glicemia Basal</span>
                    <ArrowUpDown size={11} />
                  </button>
                </th>
                <th className="py-3 px-3">
                  <button 
                    onClick={() => toggleSort("pas")} 
                    className="flex items-center gap-1 hover:text-stone-900 transition-colors cursor-pointer"
                  >
                    <span>4. Presión Arterial</span>
                    <ArrowUpDown size={11} />
                  </button>
                </th>
                <th className="py-3 px-3">5. Perfil Lipídico</th>
                <th className="py-3 px-3">
                  <button 
                    onClick={() => toggleSort("stress")} 
                    className="flex items-center gap-1 hover:text-stone-900 transition-colors cursor-pointer"
                  >
                    <span>6. Carga de Estrés</span>
                    <ArrowUpDown size={11} />
                  </button>
                </th>
                <th className="py-3 px-3">
                  <button 
                    onClick={() => toggleSort("trendPriority")} 
                    className="flex items-center gap-1 hover:text-stone-900 transition-colors cursor-pointer"
                  >
                    <span>7. Tendencia Dinámica</span>
                    <ArrowUpDown size={11} />
                  </button>
                </th>
                <th className="py-3 px-4 text-right">Acciones Clínicas</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 text-xs text-stone-700">
              {sortedUsers.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center">
                    <Users size={32} className="mx-auto text-stone-300 mb-2" />
                    <p className="font-bold text-stone-700">No se encontraron colaboradores en este clúster</p>
                    <p className="text-xs text-stone-400 mt-0.5">Pruebe ajustando los filtros de búsqueda o tendencia.</p>
                  </td>
                </tr>
              ) : (
                sortedUsers.map((item) => {
                  const ind = item.indicators;
                  const isStagnant = item.trend.status === "stagnant";

                  return (
                    <tr 
                      key={ind.userUid}
                      className={`hover:bg-amber-50/30 transition-colors ${
                        isStagnant ? "bg-amber-50/20" : ""
                      }`}
                    >
                      {/* COLUMNA 1: COLABORADOR */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 font-black text-xs shadow-sm"
                            style={{ 
                              background: isStagnant ? "#FEF3C7" : "#EBF5F7",
                              color: isStagnant ? "#92400E" : "#00628F"
                            }}
                          >
                            {ind.name.charAt(0)}
                          </div>
                          <div className="min-w-0 max-w-[180px]">
                            <p className="font-bold text-stone-900 truncate leading-snug">{ind.name}</p>
                            <p className="text-[11px] text-stone-500 truncate">{ind.email}</p>
                            <p className="text-[10px] text-stone-400 font-medium truncate mt-0.5">
                              {item.companyName} · {ind.edad} · {ind.sexo}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* COLUMNA 2: PESO & IMC */}
                      <td className="py-3.5 px-3">
                        <div className="space-y-1">
                          <div className="font-bold text-stone-900 flex items-baseline gap-1">
                            <span>{ind.imc != null ? ind.imc : "—"}</span>
                            <span className="text-[10px] text-stone-400 font-normal">kg/m²</span>
                            {ind.peso && <span className="text-[10px] text-stone-400">({ind.peso} kg)</span>}
                          </div>
                          <span 
                            className="inline-block px-2 py-0.5 rounded-md text-[10px] font-bold"
                            style={{ 
                              background: ind.imcBadge.bg, 
                              color: ind.imcBadge.color,
                              border: `1px solid ${ind.imcBadge.border}`
                            }}
                          >
                            {ind.imcBadge.label}
                          </span>
                        </div>
                      </td>

                      {/* COLUMNA 3: GRASA VISCERAL (WHtR) */}
                      <td className="py-3.5 px-3">
                        <div className="space-y-1">
                          <div className="font-bold text-stone-900 flex items-baseline gap-1">
                            <span>{ind.whtr != null ? ind.whtr : "—"}</span>
                            {ind.cintura && <span className="text-[10px] text-stone-400">({ind.cintura} cm)</span>}
                          </div>
                          <span 
                            className="inline-block px-2 py-0.5 rounded-md text-[10px] font-bold"
                            style={{ 
                              background: ind.whtrBadge.bg, 
                              color: ind.whtrBadge.color,
                              border: `1px solid ${ind.whtrBadge.border}`
                            }}
                          >
                            {ind.whtrBadge.label}
                          </span>
                        </div>
                      </td>

                      {/* COLUMNA 4: GLICEMIA BASAL */}
                      <td className="py-3.5 px-3">
                        <div className="space-y-1">
                          <div className="font-bold text-stone-900 flex items-baseline gap-1">
                            <span>{ind.glucemia != null ? ind.glucemia : "—"}</span>
                            <span className="text-[10px] text-stone-400 font-normal">mg/dL</span>
                          </div>
                          <span 
                            className="inline-block px-2 py-0.5 rounded-md text-[10px] font-bold"
                            style={{ 
                              background: ind.gluBadge.bg, 
                              color: ind.gluBadge.color,
                              border: `1px solid ${ind.gluBadge.border}`
                            }}
                          >
                            {ind.gluBadge.label}
                          </span>
                        </div>
                      </td>

                      {/* COLUMNA 5: PRESIÓN ARTERIAL */}
                      <td className="py-3.5 px-3">
                        <div className="space-y-1">
                          <div className="font-bold text-stone-900 flex items-baseline gap-1">
                            <span>{ind.pas != null ? `${ind.pas}/${ind.pad || "—"}` : "—"}</span>
                            <span className="text-[10px] text-stone-400 font-normal">mmHg</span>
                          </div>
                          <span 
                            className="inline-block px-2 py-0.5 rounded-md text-[10px] font-bold"
                            style={{ 
                              background: ind.bpBadge.bg, 
                              color: ind.bpBadge.color,
                              border: `1px solid ${ind.bpBadge.border}`
                            }}
                          >
                            {ind.bpBadge.label}
                          </span>
                        </div>
                      </td>

                      {/* COLUMNA 6: PERFIL LIPÍDICO / HEMODINÁMICA */}
                      <td className="py-3.5 px-3">
                        <div className="space-y-1">
                          <div className="text-[11px] font-bold text-stone-900">
                            {ind.colesterol ? (
                              <span>Col: {ind.colesterol} / TG: {ind.trigliceridos || "—"}</span>
                            ) : (
                              <span>FC: {ind.hr || "—"} lpm / SpO2: {ind.spo2 || "—"}%</span>
                            )}
                          </div>
                          <span 
                            className="inline-block px-2 py-0.5 rounded-md text-[10px] font-bold"
                            style={{ 
                              background: ind.lipidBadge.bg, 
                              color: ind.lipidBadge.color,
                              border: `1px solid ${ind.lipidBadge.border}`
                            }}
                          >
                            {ind.lipidBadge.label}
                          </span>
                        </div>
                      </td>

                      {/* COLUMNA 7: CARGA DE ESTRÉS (BAEVSKY) */}
                      <td className="py-3.5 px-3">
                        <div className="space-y-1">
                          <div className="font-bold text-stone-900 flex items-baseline gap-1">
                            <span>{ind.stressIndex != null ? Math.round(ind.stressIndex) : "—"}</span>
                            <span className="text-[10px] text-stone-400 font-normal">pts</span>
                          </div>
                          <span 
                            className="inline-block px-2 py-0.5 rounded-md text-[10px] font-bold"
                            style={{ 
                              background: ind.stressBadge.bg, 
                              color: ind.stressBadge.color,
                              border: `1px solid ${ind.stressBadge.border}`
                            }}
                          >
                            {ind.stressBadge.label}
                          </span>
                        </div>
                      </td>

                      {/* COLUMNA 8: COMPORTAMIENTO DINÁMICO & ESTANCAMIENTO */}
                      <td className="py-3.5 px-3">
                        <div className="space-y-1">
                          <span 
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border ${item.trend.badgeClass}`}
                            title={item.trend.detail}
                          >
                            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: item.trend.dotColor }} />
                            <span>{item.trend.label}</span>
                          </span>
                          <p className="text-[10px] text-stone-500 font-medium max-w-[140px] truncate" title={item.trend.detail}>
                            {item.trend.sublabel}
                          </p>
                        </div>
                      </td>

                      {/* COLUMNA 9: ACCIONES CLÍNICAS */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Botón Coordinar IHO */}
                          <button
                            onClick={() => onOpenIhoModal(item.rawUser, ind, item.trend)}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-[11px] font-bold text-amber-900 bg-amber-100/80 hover:bg-amber-200/90 border border-amber-300 transition-all cursor-pointer active:scale-95"
                            title="Emitir directiva médica presencial para el Oficial de Salud (IHO)"
                          >
                            <ShieldAlert size={12} className="text-amber-700" />
                            <span>IHO</span>
                          </button>

                          {/* Botón Ver Expediente 13 Biomarcadores */}
                          <button
                            onClick={() => onSelectUserForRecord(item.rawUser)}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-[11px] font-bold text-[#00628F] bg-[#EBF5F7] hover:bg-[#D5ECF0] border border-cyan-200 transition-all cursor-pointer active:scale-95"
                            title="Abrir expediente individual detallado con los 13 biomarcadores PageSpeed"
                          >
                            <span>Expediente</span>
                            <ChevronRight size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
